'use client';
import React, { useMemo } from 'react';
import { useBreadcrumb } from 'react-instantsearch';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@/components/breadcrumbs/breadcrumbs';
import Link from 'next/link';
import { useProductSearch } from '@/app/components/context/product-search-context';
import JSONLD from '@/components/json-ld/json-ld';

const ProductsBreadcrumbs = ({
  parentCategoryId,
  childCategoryId,
}: {
  parentCategoryId?: string;
  childCategoryId?: string;
}) => {
  const { items } = useBreadcrumb({ attributes: ['categories.lv0', 'categories.lv1'] });
  const { categoryItems } = useProductSearch();

  const parentCategory = categoryItems.find((c) => c.id === parentCategoryId);

  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = [
      {
        position: 1,
        href: '/',
        name: 'Home',
      },
      {
        position: 2,
        href: '/assortiment',
        name: 'Assortiment',
      },
    ];

    if (parentCategory) {
      baseBreadcrumbs.push({
        position: 3,
        href: parentCategory?.pathname,
        name: parentCategory.name!,
      });
    }

    const childCategory = parentCategory?.items?.find((c) => c.id === childCategoryId);
    if (childCategory) {
      baseBreadcrumbs.push({
        position: 3,
        href: childCategory?.pathname,
        name: childCategory.name!,
      });
    }

    return baseBreadcrumbs;
  }, [items, parentCategory]);

  const jsonLdData = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((breadcrumb) => ({
      '@type': 'ListItem',
      position: breadcrumb.position,
      item: {
        '@id': breadcrumb.href,
        name: breadcrumb.name,
      },
    })),
  };

  return (
    <>
      <JSONLD data={jsonLdData} />
      <Breadcrumb>
        {breadcrumbs.map((breadcrumb, index) => (
          <BreadcrumbItem key={index} isCurrentPage={index === breadcrumbs.length - 1}>
            <BreadcrumbLink as={Link} href={breadcrumb.href}>
              {breadcrumb.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
        ))}
      </Breadcrumb>
    </>
  );
};

export default ProductsBreadcrumbs;
